import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, ArrowLeft, Check, HelpCircle, MapPin } from "lucide-react";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ASSETS = {
  logoWhite: "https://customer-assets.emergentagent.com/job_venue-revenue/artifacts/rcizesv6_AnytimeFumeWhiteLOGO.png",
};

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const SupportPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    machine_location: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Map subject to issue_type for backend compatibility
      await axios.post(`${API}/support`, {
        ...formData,
        issue_type: formData.subject
      });
      setIsSubmitted(true);
      toast.success("Support request submitted!");
    } catch (error) {
      console.error("Error submitting:", error);
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <Toaster 
        position="top-center" 
        toastOptions={{
          style: {
            background: '#0a0a0a',
            color: '#ffffff',
            border: '1px solid rgba(255,255,255,0.1)',
          },
        }}
      />
      
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <img src={ASSETS.logoWhite} alt="Anytime Fume" className="h-8 w-auto" />
            <span className="font-body text-lg tracking-[0.15em] text-white">ANYTIME FUME</span>
          </a>
          <a 
            href="/" 
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm font-body"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </a>
        </div>
      </header>

      <main className="py-12 md:py-20">
        <div className="max-w-2xl mx-auto px-6">
          {/* Header */}
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.div variants={fadeInUp} className="mb-4">
              <div className="inline-flex items-center justify-center w-16 h-16 border border-white/20 text-white/60 mb-6">
                <HelpCircle className="w-7 h-7" />
              </div>
            </motion.div>
            <motion.h1 variants={fadeInUp} className="font-heading text-4xl md:text-5xl text-white mb-4">
              Customer Support
            </motion.h1>
            <motion.p variants={fadeInUp} className="font-body text-white/60 text-base max-w-md mx-auto">
              Had an issue with your fragrance spray? We're here to help. Fill out the form below and we'll get back to you promptly.
            </motion.p>
          </motion.div>

          {/* Contact Email */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-12"
          >
            <a 
              href="mailto:tsyon@anytimefume.com.au"
              className="flex items-center justify-center gap-3 p-4 border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/20 transition-all group"
            >
              <Mail className="w-5 h-5 text-white/50 group-hover:text-white transition-colors" />
              <div className="text-center">
                <p className="font-body text-white text-sm">tsyon@anytimefume.com.au</p>
                <p className="font-body text-white/40 text-xs">Head of Partnerships</p>
              </div>
            </a>
          </motion.div>

          {/* Form Section */}
          {isSubmitted ? (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-16 border border-white/10 bg-white/[0.02]"
            >
              <div className="w-16 h-16 border border-green-500/30 bg-green-500/10 flex items-center justify-center text-green-400 mx-auto mb-6">
                <Check className="w-8 h-8" />
              </div>
              <h2 className="font-heading text-3xl text-white mb-3">Request Submitted</h2>
              <p className="font-body text-white/60 max-w-md mx-auto mb-6">
                Thank you for reaching out. We've received your support request and will get back to you within 24 hours.
              </p>
              <a 
                href="/"
                className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors font-body text-sm"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </a>
            </motion.div>
          ) : (
            <motion.div 
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
              className="border border-white/10 bg-white/[0.02] p-8 md:p-10"
            >
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="support-form">
                {/* Name & Email */}
                <motion.div variants={fadeInUp} className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block font-body text-white/60 text-xs uppercase tracking-wider mb-2">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      data-testid="support-input-name"
                      className="w-full bg-black border border-white/20 focus:border-white px-4 py-3 text-white placeholder:text-white/30 focus:outline-none transition-colors font-body text-sm"
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label className="block font-body text-white/60 text-xs uppercase tracking-wider mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      data-testid="support-input-email"
                      className="w-full bg-black border border-white/20 focus:border-white px-4 py-3 text-white placeholder:text-white/30 focus:outline-none transition-colors font-body text-sm"
                      placeholder="john@example.com"
                    />
                  </div>
                </motion.div>

                {/* Phone & Location */}
                <motion.div variants={fadeInUp} className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block font-body text-white/60 text-xs uppercase tracking-wider mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      data-testid="support-input-phone"
                      className="w-full bg-black border border-white/20 focus:border-white px-4 py-3 text-white placeholder:text-white/30 focus:outline-none transition-colors font-body text-sm"
                      placeholder="0400 000 000"
                    />
                  </div>
                  <div>
                    <label className="block font-body text-white/60 text-xs uppercase tracking-wider mb-2">
                      Machine Location
                    </label>
                    <input
                      type="text"
                      name="machine_location"
                      value={formData.machine_location}
                      onChange={handleChange}
                      data-testid="support-input-location"
                      className="w-full bg-black border border-white/20 focus:border-white px-4 py-3 text-white placeholder:text-white/30 focus:outline-none transition-colors font-body text-sm"
                      placeholder="e.g., Fitness First Melbourne CBD"
                    />
                  </div>
                </motion.div>

                {/* Subject */}
                <motion.div variants={fadeInUp}>
                  <label className="block font-body text-white/60 text-xs uppercase tracking-wider mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    data-testid="support-input-subject"
                    className="w-full bg-black border border-white/20 focus:border-white px-4 py-3 text-white placeholder:text-white/30 focus:outline-none transition-colors font-body text-sm"
                    placeholder="e.g., Refund request, Spray didn't work, etc."
                  />
                </motion.div>

                {/* Message */}
                <motion.div variants={fadeInUp}>
                  <label className="block font-body text-white/60 text-xs uppercase tracking-wider mb-2">
                    Describe the Issue *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    data-testid="support-input-message"
                    className="w-full bg-black border border-white/20 focus:border-white px-4 py-3 text-white placeholder:text-white/30 focus:outline-none transition-colors font-body text-sm resize-none"
                    placeholder="Please provide details about what happened..."
                  />
                </motion.div>

                <motion.div variants={fadeInUp} className="pt-2">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="support-submit-btn"
                    className="w-full bg-white text-black py-4 text-sm font-medium tracking-wider uppercase hover:bg-white/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? "Submitting..." : "Submit Request"}
                  </button>
                </motion.div>
              </form>
            </motion.div>
          )}

          {/* Back Link */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-10 text-center"
          >
            <p className="font-body text-white/40 text-sm">
              Looking to partner with us?{" "}
              <a href="/partner" className="text-white/70 hover:text-white underline">Partnership enquiries</a>
            </p>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="font-body text-white/30 text-xs">
            © {new Date().getFullYear()} Anytime Fume Pty Ltd. ABN: 25 693 934 217
          </p>
        </div>
      </footer>
    </div>
  );
};

export default SupportPage;
